import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { FaCheck, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useMobileDetector } from '../utils/deviceDetection';

// Feature data organized by category
const featureCategories = [
  {
    id: 'customerManagement',
    title: 'Customer Management',
    description: 'Everything you need to build stronger customer relationships',
    features: [
      {
        title: 'Customer Profiles',
        description: 'Create and manage detailed customer profiles with contact info, service history, property details, and billing information all in one place.',
        icon: '👤'
      },
      {
        title: 'Customer Map',
        description: 'View all your customers on an interactive map to easily see service areas and plan efficient routes.',
        icon: '🗺️'
      },
      {
        title: 'Two-Way Communication',
        description: 'Chat directly with customers through the app with our built-in messaging system - keep all communications in one place.',
        icon: '💬'
      }
    ]
  },
  {
    id: 'jobManagement',
    title: 'Job Management',
    description: 'Streamline your workflow with powerful job tools',
    features: [
      {
        title: 'Job Creation & Tracking',
        description: 'Create, assign, and track jobs from start to finish. Set job types, pricing, and special instructions for your team.',
        icon: '📋'
      },
      {
        title: 'Drag-and-Drop Scheduling',
        description: 'Easily schedule jobs with our intuitive drag-and-drop calendar interface. Reschedule with a simple drag when plans change.',
        icon: '📅'
      },
      {
        title: 'Route Optimization',
        description: 'Save time and fuel with automated route optimization that finds the most efficient path between jobs.',
        icon: '🚗'
      }
    ]
  },
  {
    id: 'financialTools',
    title: 'Financial Tools',
    description: 'Streamline your billing and get paid faster',
    features: [
      {
        title: 'Invoicing',
        description: 'Create professional invoices in seconds and send them directly to your customers. Track payment status and send automated reminders.',
        icon: '📄'
      },
      {
        title: 'Quote Builder',
        description: 'Build and send professional quotes to potential customers. Convert accepted quotes to jobs with a single click.',
        icon: '💲'
      },
      {
        title: 'Analytics Dashboard',
        description: 'Gain insights into your business performance with detailed financial reports and analytics.',
        icon: '📊'
      }
    ]
  },
  {
    id: 'teamFeatures',
    title: 'Team Collaboration',
    description: 'Keep your team organized and on the same page',
    features: [
      {
        title: 'Employee Profiles',
        description: 'Create profiles for each team member, track assigned jobs, and monitor performance. Employees see their daily route at a glance.',
        icon: '👥'
      },
      {
        title: 'Team Notifications',
        description: 'Automatically notify team members about new jobs, schedule changes, and customer requests.',
        icon: '🔔'
      },
      {
        title: 'Workflow Automation',
        description: 'Automate repetitive tasks and notifications to save time and reduce errors in your daily operations.',
        icon: '⚙️'
      }
    ]
  }
];

// Expandable feature section for mobile view
const ExpandableFeatureCategory = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="border-b border-gray-700 py-4">
      <button 
        className="w-full flex justify-between items-center text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">{category.features[0].icon}</span>
          <h3 className="text-xl font-bold text-white">{category.title}</h3>
        </div>
        {isExpanded ? (
          <FaChevronUp className="text-blue-400" />
        ) : (
          <FaChevronDown className="text-blue-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="mt-4 ml-10 space-y-6">
          <p className="text-blue-200 mb-4">{category.description}</p>
          
          {category.features.map((feature, index) => (
            <div key={index} className="bg-surface/20 p-4 rounded-lg">
              <div className="flex items-start mb-2">
                <span className="text-xl mr-2">{feature.icon}</span>
                <h4 className="text-white font-medium">{feature.title}</h4>
              </div>
              <p className="text-blue-100 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Desktop Feature Item component - enhanced contrast
const FeatureItem = ({ feature }) => (
  <div className="bg-surface/40 backdrop-blur-sm rounded-lg p-6 border border-gray-700 hover:border-blue-500 transition duration-300 hover:shadow-lg hover:shadow-blue-500/20 h-full flex flex-col">
    <div className="bg-blue-500/10 rounded-full p-3 w-14 h-14 flex items-center justify-center mb-4">
      <span className="text-3xl">{feature.icon}</span>
    </div>
    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
    <p className="text-blue-100 flex-grow">{feature.description}</p>
  </div>
);

// Desktop features category component
const FeatureCategory = ({ category, index }) => (
  <section className="py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-white mb-4 relative">
          <span className="relative z-10">{category.title}</span>
          <span className="absolute inset-0 blur-md bg-blue-500/30 transform -skew-x-12 z-0"></span>
        </h2>
        <p className="text-xl text-blue-200 max-w-3xl mx-auto">{category.description}</p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {category.features.map((feature, featureIndex) => (
          <FeatureItem key={featureIndex} feature={feature} />
        ))}
      </div>
    </div>
  </section>
);

const Features = () => {
  const isMobile = useMobileDetector();
  
  // Mobile optimized Features page with expandable sections
  const MobileFeaturesPage = () => (
    <div className="px-4 py-8">
      {/* Hero Section - Always visible */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
          Features
        </h1>
        <p className="text-blue-100">
          Everything you need to run your lawn care business efficiently
        </p>
      </div>
      
      {/* Expandable Feature Categories */}
      <div className="space-y-2 mb-8">
        {featureCategories.map((category, index) => (
          <ExpandableFeatureCategory key={index} category={category} />
        ))}
      </div>
      
      {/* CTA */}
      <div className="mt-8 bg-gradient-to-r from-blue-900/50 to-purple-900/50 p-5 rounded-lg text-center">
        <h3 className="text-white font-semibold mb-2">Ready to transform your lawn care business?</h3>
        <Link 
          to="/pricing" 
          className="inline-block px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition duration-200 mt-2"
        >
          View Pricing Plans
        </Link>
      </div>
    </div>
  );
  
  // Desktop Features Page
  const DesktopFeaturesPage = () => (
    <>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/20 to-purple-500/20 transform rotate-12"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-6">
              Powerful Features for Lawn Care Professionals
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-blue-100 leading-relaxed">
              Everything you need to streamline your operations, delight your customers, and grow your business.
            </p>
            <div className="mt-10">
              <Link 
                to="/pricing" 
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-lg transition duration-200 transform hover:scale-105 shadow-lg hover:shadow-blue-500/50"
              >
                View Pricing Plans
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Feature Categories */}
      {featureCategories.map((category, index) => (
        <FeatureCategory key={index} category={category} index={index} />
      ))}
      
      {/* Integration Section */}
      <section className="py-16 bg-gradient-to-b from-surface/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Seamless Integrations</h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Connect AutoLawn with your favorite tools and services
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Stripe', 'QuickBooks', 'Google Calendar', 'Mailchimp', 'Zapier', 'Twilio', 'Square', 'Google Maps'].map((integration, index) => (
              <div key={index} className="bg-surface/20 backdrop-blur-sm rounded-lg p-4 border border-gray-700 flex items-center justify-center h-24">
                <span className="text-white font-medium">{integration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-xl p-10 text-center backdrop-blur-sm border border-blue-700/50">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to transform your lawn care business?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of professionals who trust AutoLawn to manage their daily operations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/register" 
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-lg transition duration-200 transform hover:scale-105 shadow-lg hover:shadow-blue-500/50"
              >
                Start Free Trial
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg transition duration-200"
              >
                Request Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
  
  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      <main>
        {isMobile ? <MobileFeaturesPage /> : <DesktopFeaturesPage />}
      </main>
      <Footer />
    </div>
  );
};

export default Features;
